// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useAllDisablementHistory from '@hooks/queries/registry/transferEnablement/useAllDisablementHistory';
import {
  InstrumentsById,
  useInstrumentConfigurationsById,
} from '@hooks/queries/useGetAllInstrumentConfigurations';
import React from 'react';
import { Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CopyableText from '../../../components/CopyableText';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useCsvExport } from '../../../hooks/other/useCsvExport';
import { DisablementHistoryEntry } from '../../../utils/LedgerApiClientV2';
import { formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';

type Row = DisablementHistoryEntry;

interface TransferEnablementHistoryProps {
  disableVirtualization?: boolean;
}

const formatDisablementAction = (action: string): string =>
  action === 'DisablementAdded' ? 'Disablement Added' : 'Disablement Removed';

const createHeaders = (instrumentsById: InstrumentsById): GridColDef<Row>[] => [
  {
    field: 'instrumentId',
    headerName: 'Instrument Id',
    valueGetter: (params) => params.row.instrumentId,
    renderCell: (params) => <CopyableText value={params.row.instrumentId} />,
    flex: 1,
    filterable: true,
  },
  {
    field: 'tokenName',
    headerName: 'Token Name',
    valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.tokenName || '',
    flex: 1,
    filterable: true,
  },
  {
    field: 'tokenSymbol',
    headerName: 'Token Symbol',
    valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.tokenSymbol || '',
    flex: 1,
    filterable: true,
  },
  {
    field: 'cusip',
    headerName: 'CUSIP',
    valueGetter: (params) => instrumentsById[params.row.instrumentId]?.payload.cusip || '',
    flex: 1,
    filterable: true,
  },
  {
    field: 'action',
    headerName: 'Action',
    valueGetter: (params) => formatDisablementAction(params.row.action),
    flex: 0.75,
    filterable: true,
  },
  {
    field: 'actor',
    headerName: 'Actor',
    valueGetter: (params) => params.row.actor,
    renderCell: (params) => <PartyDisplay party={params.row.actor} />,
    flex: 1,
    filterable: false,
  },
  {
    field: 'executedAt',
    headerName: 'Executed At',
    type: 'dateTime',
    valueGetter: (params) => parseTimestampToDate(params.row.createdAt),
    renderCell: (params) => formatTimestamp(params.row.createdAt),
    flex: 1,
    filterable: true,
  },
];

export const TransferEnablementHistory: React.FC<TransferEnablementHistoryProps> = ({
  disableVirtualization,
}) => {
  const allDisablementHistory = useAllDisablementHistory();
  const instrumentsById = useInstrumentConfigurationsById();

  const rows = allDisablementHistory.data ?? [];

  const columns = React.useMemo(() => createHeaders(instrumentsById), [instrumentsById]);
  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'transfer_enablement_history',
  });

  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
  ];

  return (
    <PageWrapper
      buttons={buttons}
      pageName={
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
          <Typography
            sx={{ textTransform: 'capitalize' }}
            fontWeight="bold"
            color="primary"
            variant="h4"
          >
            Transfer Enablement History
          </Typography>
        </Box>
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.contractId}
        defaultSortModel={[{ field: 'executedAt', sort: 'desc' }]}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
